import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumb, Tabs, TabsProps } from 'antd'
import { BreadcrumbItemType, BreadcrumbSeparatorType } from 'antd/es/breadcrumb/Breadcrumb'
import PricelistsTable from './PricelistsTable'
import ItemRender from 'components/breadcrumb/ItemRender'
import Card from 'components/card/Card'
import Pagination from 'components/pagination/Pagination'
import Errors from 'components/errors/Errors'
import ButtonLink from 'components/button/Button'

import {
  AdminsEcommerceProductPricelistListResponseItem,
  AdminsEcommerceProductPricelistListSortEnum,
  EcommerceProductStatusEnum,
  OrderDirectionEnum,
} from 'generated/api'

interface IProductPriceIndexProps {
  isLoading: boolean
  isFailed: boolean
  data: AdminsEcommerceProductPricelistListResponseItem[]
  totalRows: number
  page: number
  limit: number
  navigationsItem: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[]
  orderColumn: AdminsEcommerceProductPricelistListSortEnum
  orderDirection: OrderDirectionEnum
  status: EcommerceProductStatusEnum
  onChangeProductsPriceStatus: (status: EcommerceProductStatusEnum) => void
  onChangePage: (page: number) => void
  onChangeLimitPage: (limit: number) => void
  onFetchProductPrice: (id: string) => void
  onChangeSortStockPrice: (columnKey: AdminsEcommerceProductPricelistListSortEnum) => void
  onClickCreate: () => void
  onClickEdit: (productPriceStock: AdminsEcommerceProductPricelistListResponseItem) => void
}

export default function PricelistsIndex(props: IProductPriceIndexProps) {
  const { id } = useParams()

  const {
    isLoading,
    isFailed,
    data,
    totalRows,
    page,
    limit,
    navigationsItem,
    orderColumn,
    orderDirection,
    status,
    onChangeProductsPriceStatus,
    onChangePage,
    onChangeLimitPage,
    onFetchProductPrice,
    onChangeSortStockPrice,
    onClickCreate,
    onClickEdit,
  } = props

  useEffect(() => {
    onFetchProductPrice(id as string)
  }, [])

  const tabs: TabsProps['items'] = [
    {
      key: EcommerceProductStatusEnum.Active,
      label: 'PRODUK AKTIF',
    },
    {
      key: EcommerceProductStatusEnum.Inactive,
      label: 'RIWAYAT',
    },
  ]

  return (
    <Card
      loading={isLoading}
      dataTestId="card_stock_price"
      spinnerTestId="loader_stock_price"
      className="rounded-xl p-0 pb-2"
    >
      {isFailed && !isLoading ? (
        <Errors
          dataTestId="pricelist"
          inverse={false}
          header="Mohon maaf, permintaan Anda tidak dapat diproses."
          paragraph="terjadi kesalahan internal pada server kami. Silakan coba beberapa saat lagi."
          button={true}
          buttonLabel="Coba lagi"
          buttonAction={() => onFetchProductPrice(id as string)}
        />
      ) : (
        <>
          <Card.Header className="sticky-card-header block xl:flex py-2 px-4 justify-between border-b border-solid border-[#E5E5E7]">
            <div className="mb-0 flex items-center">
              <Breadcrumb
                className="py-3.5"
                itemRender={route => ItemRender(route)}
                items={navigationsItem}
                separator=">"
              />
            </div>
            <div className="xl:w-auto w-full">
              <ButtonLink
                data-testid="button_create_product_stock_price"
                variant="solid-primary"
                block={true}
                label="Tambah"
                className="py-3 px-4 text-sm font-bold h-full"
                onClick={onClickCreate}
              />
            </div>
          </Card.Header>
          <Tabs
            defaultActiveKey={status}
            items={tabs}
            onChange={key => onChangeProductsPriceStatus(key as EcommerceProductStatusEnum)}
          />
          <Card.Body className="mt-0">
            <div className="flex flex-col gap-4">
              <PricelistsTable
                loading={false}
                data={data}
                orderColumn={orderColumn}
                orderDirection={orderDirection}
                setSortingOrder={onChangeSortStockPrice}
                onClickEdit={onClickEdit}
              />
            </div>
          </Card.Body>
          <Card.Footer>
            <div className="px-4 py-3">
              <Pagination
                disabled={isLoading}
                setLimit={(limit: number) => onChangeLimitPage(limit)}
                onPageChanged={(page: number) => onChangePage(page)}
                totalRows={totalRows}
                currentPage={page}
                limit={limit}
                showLimitPage={true}
              />
            </div>
          </Card.Footer>
        </>
      )}
    </Card>
  )
}
